// Review marks as content anchors (docs/design.md "Review marks"): a mark
// is `(path, side, blob, line range)`, the lines a reviewer read, never a
// layout-derived key. This module builds anchors and plans the writes
// that mark or unmark lines; `model.ts` computes what they cover.
import type { Anchor, FileSides, Side } from '../engine/types';
import type { WireAnchor } from '../api/server';

export type { Anchor };

/** An anchor as the marks API sends it. */
export function toWire(a: Anchor): WireAnchor {
  const w: WireAnchor = { path: a.path, side: a.side, blob: a.blob, start_line: a.startLine, end_line: a.endLine };
  if (a.origins?.length) w.origins = [...a.origins];
  return w;
}

/** An anchor from the marks API. */
export function fromWire(w: WireAnchor): Anchor {
  const a: Anchor = { path: w.path, side: w.side, blob: w.blob, startLine: w.start_line, endLine: w.end_line };
  if (w.origins?.length) a.origins = [...w.origins];
  return a;
}

/** The anchors' origins, joined, sorted; undefined for none. */
function originsOf(anchors: Iterable<Anchor>): string[] | undefined {
  const all = new Set<string>();
  for (const a of anchors) for (const k of a.origins ?? []) all.add(k);
  return all.size ? [...all].sort() : undefined;
}

function withOrigins(a: Anchor, origins: string[] | undefined): Anchor {
  return origins ? { ...a, origins } : a;
}

/** A changed line: its side and 1-based number there. */
export interface Line {
  side: Side;
  line: number;
}

/**
 * Git's object ID of `text` as a blob: SHA-1 of `blob <bytes>\0` and its
 * UTF-8 bytes, 40 lowercase hex digits. For a text decoded from a blob it
 * is that blob's SHA, unless decoding changed the bytes (a byte-order mark
 * dropped, invalid UTF-8 replaced); either way equal texts get equal IDs,
 * which is what an anchor needs.
 */
export async function blobId(text: string): Promise<string> {
  const body = new TextEncoder().encode(text);
  const head = new TextEncoder().encode(`blob ${body.length}\0`);
  const all = new Uint8Array(head.length + body.length);
  all.set(head);
  all.set(body, head.length);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-1', all));
  return Array.from(digest, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** An anchor's identity: equal anchors (same path, side, blob and range)
 *  have the same ID, and the server stores one mark per ID. */
export function anchorId(a: Anchor): string {
  return `${a.side}:${a.blob}:${a.startLine}-${a.endLine}:${a.path}`;
}

/** Anchors for `lines` on `sides`: one per maximal run of consecutive line
 *  numbers on a side, old side first. Lines on a side the file lacks are
 *  dropped. */
export function anchorsFor(sides: FileSides, lines: Iterable<Line>): Anchor[] {
  const out: Anchor[] = [];
  for (const side of ['old', 'new'] as const) {
    const at = sides[side];
    if (!at) continue;
    const nums = [...new Set([...lines].filter((l) => l.side === side).map((l) => l.line))].sort((a, b) => a - b);
    for (const n of nums) {
      const last = out[out.length - 1];
      if (last && last.side === side && last.endLine + 1 === n) last.endLine = n;
      else out.push({ path: at.path, side, blob: at.blob, startLine: n, endLine: n });
    }
  }
  return out;
}

/** The marks to remove (`unset`) and then store (`set`), as anchors. */
export interface Plan {
  set: Anchor[];
  unset: Anchor[];
}

/**
 * Mark `lines` of a file reviewed: anchors on its current blobs. Stored
 * anchors on the same blobs that overlap or adjoin them are merged in
 * (replaced by one anchor per maximal run), so a blob's marks stay few.
 */
export function planMark(sides: FileSides, marks: readonly Anchor[], lines: Iterable<Line>): Plan {
  const wanted = [...lines];
  const unset: Anchor[] = [];
  const all: Line[] = [...wanted];
  for (const side of ['old', 'new'] as const) {
    const at = sides[side];
    if (!at) continue;
    const mine = new Set(wanted.filter((l) => l.side === side).map((l) => l.line));
    if (!mine.size) continue;
    for (const m of marks) {
      if (m.side !== side || m.path !== at.path || m.blob !== at.blob) continue;
      let touches = false;
      for (let n = m.startLine - 1; n <= m.endLine + 1 && !touches; n++) touches = mine.has(n);
      if (!touches) continue;
      unset.push(m);
      for (let n = m.startLine; n <= m.endLine; n++) all.push({ side, line: n });
    }
  }
  // A merged anchor holds the lines of those it absorbs, and their origins.
  const set = anchorsFor(sides, all).map((a) =>
    withOrigins(a, originsOf(unset.filter((m) => m.side === a.side && m.startLine <= a.endLine && a.startLine <= m.endLine))),
  );
  const kept = new Set(set.map(anchorId));
  return { set, unset: unset.filter((a) => !kept.has(anchorId(a))) };
}

/**
 * Unmark `lines` of a file: every stored anchor that covers one of them
 * (directly, or through `carry` from an earlier blob) is removed, and the
 * rest of its lines are stored again on its own blob, so only the asked
 * lines stop being covered. `carry(anchor)` gives, for an anchor on another
 * blob, each of its lines' line in the file's current blob (0 for none),
 * or null when the anchor does not apply to the file.
 */
export function planUnmark(
  sides: FileSides,
  marks: readonly Anchor[],
  lines: Iterable<Line>,
  carry: (a: Anchor, side: Side) => readonly number[] | null,
): Plan {
  const off = { old: new Set<number>(), new: new Set<number>() };
  for (const l of lines) off[l.side].add(l.line);
  const set: Anchor[] = [];
  const unset: Anchor[] = [];
  for (const m of marks) {
    const at = sides[m.side];
    if (!at || !off[m.side].size) continue;
    let image: readonly number[] | null;
    if (m.blob === at.blob && m.path === at.path) {
      image = Array.from({ length: m.endLine - m.startLine + 1 }, (_, k) => m.startLine + k);
    } else image = carry(m, m.side);
    if (!image || !image.some((n) => off[m.side].has(n))) continue;
    unset.push(m);
    const rest: Line[] = [];
    image.forEach((n, k) => {
      if (!off[m.side].has(n)) rest.push({ side: m.side, line: m.startLine + k });
    });
    set.push(...anchorsFor({ old: null, new: null, [m.side]: { path: m.path, blob: m.blob } }, rest).map((a) => withOrigins(a, originsOf([m]))));
  }
  return { set, unset };
}
